import type { ItemInBasket, ItemInBasketOptionChoice } from "../types/firestore/basket.js"
import type { Option, OptionChoice } from "../types/firestore/option.js"

export type OptionSelection = Record<string, number>

export const getOptionChoiceQuantity = (choice: { quantity?: number }) => choice.quantity ?? 1

export const getOptionChoiceQuantities = (choice: Pick<OptionChoice, "quantityChoices">) =>
  [...new Set(choice.quantityChoices ?? [])]
    .filter(quantity => Number.isSafeInteger(quantity) && quantity > 0)
    .sort((a, b) => a - b)

export const isOptionChoiceQuantitiesValid = (choice: Pick<OptionChoice, "quantityChoices">, max: number) =>
  choice.quantityChoices === undefined ||
  choice.quantityChoices === null ||
  (choice.quantityChoices.length > 0 &&
    getOptionChoiceQuantities(choice).length === choice.quantityChoices.length &&
    choice.quantityChoices.every(quantity => quantity <= max))

/** Number of parent units to which the selected extras apply. */
export const getOptionParentQuantity = (
  item: Pick<ItemInBasket, "localUnit" | "quantity" | "kolliContent" | "kolliQuantity">
) =>
  item.localUnit === "carton" && item.kolliContent
    ? (item.kolliQuantity ?? 1) *
      (item.kolliContent.contentUnit === "piece"
        ? item.kolliContent.contentQuantity
        : item.kolliContent.contentQuantity / 1000)
    : item.localUnit === "piece"
      ? item.quantity
      : item.quantity / 1000

export const getOptionChoicesPrice = (choices: { price?: number; quantity?: number }[]) =>
  choices.reduce((total, choice) => total + (choice.price ?? 0) * getOptionChoiceQuantity(choice), 0)

export const getOptionChoicesDeposit = (choices: Pick<ItemInBasketOptionChoice, "deposit" | "quantity">[]) =>
  choices.reduce(
    (total, choice) =>
      total + (choice.deposit?.type !== "none" ? choice.deposit?.value ?? 0 : 0) * getOptionChoiceQuantity(choice),
    0
  )

export const formatOptionChoiceName = (choice: { name: string; quantity?: number }) =>
  `${getOptionChoiceQuantity(choice) > 1 ? `${getOptionChoiceQuantity(choice)} × ` : ""}${choice.name}`

export const isOptionSelectionValid = (option: Option, choices: { choiceId: string; quantity?: number }[]) => {
  if (new Set(choices.map(choice => choice.choiceId)).size !== choices.length) return false
  if (
    choices.some(choice => {
      const quantity = getOptionChoiceQuantity(choice)
      const available = option.choices.find(
        candidate => candidate.id === choice.choiceId && candidate.visible !== false
      )
      return (
        !Number.isSafeInteger(quantity) ||
        quantity <= 0 ||
        (option.selection !== "quantity" && quantity !== 1) ||
        !available ||
        (option.selection === "quantity" &&
          available.quantityChoices !== undefined &&
          available.quantityChoices !== null &&
          (!isOptionChoiceQuantitiesValid(available, option.max) || !available.quantityChoices.includes(quantity)))
      )
    })
  )
    return false

  const total = choices.reduce((sum, choice) => sum + getOptionChoiceQuantity(choice), 0)
  return option.selection === "single" ? total === 1 : total >= option.min && total <= option.max
}

export const createSelectedOptionChoices = (
  option: Option,
  optionId: string,
  selection: OptionSelection
): ItemInBasketOptionChoice[] =>
  option.choices.flatMap(choice => {
    const quantity = selection[choice.id] ?? 0
    if (quantity <= 0) return []
    const { id, position, quantityChoices, ...snapshot } = choice
    return [{ ...snapshot, choiceId: id, optionId, ...(option.selection === "quantity" ? { quantity } : {}) }]
  })
